if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.isMediaViewer = this.classList.contains('media-viewer');

        if (this.isMediaViewer) {
          this.stage = this.querySelector('.media-viewer__stage');
          this.cursorIcon = this.querySelector('.media-viewer__cursor-icon');
          this.hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)');
          this.activePointers = new Map();
          this.onMouseMove = this.onMouseMove.bind(this);
          this.onPinchStart = this.onPinchStart.bind(this);
          this.onPinchMove = this.onPinchMove.bind(this);
          this.onPinchEnd = this.onPinchEnd.bind(this);
          this.onResize = this.onResize.bind(this);
          window.addEventListener('resize', this.onResize);
          this.querySelectorAll('.media-viewer__rail-thumb').forEach((button) => {
            /* ModalDialog (super) closes on any pointerup that isn't inside
               deferred-media/product-model — stop it here so picking a
               thumbnail doesn't also close the modal. */
            button.addEventListener('pointerup', (event) => event.stopPropagation());
            button.addEventListener('click', () => this.setActiveMedia(button.getAttribute('data-media-id')));
          });
        }
      }

      hide() {
        super.hide();
        if (this.isMediaViewer && this.stage) {
          this.stage.removeEventListener('mousemove', this.onMouseMove);
          this.stage.removeEventListener('pointerdown', this.onPinchStart);
          this.stage.style.cursor = '';
        }
        window.removeEventListener('pointermove', this.onPinchMove);
        window.removeEventListener('pointerup', this.onPinchEnd);
        window.removeEventListener('pointercancel', this.onPinchEnd);
        if (this.activePointers) this.activePointers.clear();
        this.gestureMode = null;
        this.gestureImage = null;
      }

      show(opener) {
        super.show(opener);

        if (this.isMediaViewer) {
          this.setActiveMedia(opener.getAttribute('data-media-id'));
          if (this.stage) {
            if (this.hoverCapable.matches) {
              this.stage.addEventListener('mousemove', this.onMouseMove);
              if (this.cursorIcon) this.stage.style.cursor = 'none';
            } else {
              this.stage.addEventListener('pointerdown', this.onPinchStart);
            }
          }
        } else {
          this.showActiveMedia();
        }
      }

      /* ---- image_zoom: 'fullscreen' — full-width hover-scrub viewer ---- */

      setActiveMedia(mediaId) {
        this.querySelectorAll('.media-viewer__slide').forEach((slide) => {
          slide.classList.toggle('active', slide.getAttribute('data-media-id') === mediaId);
        });
        this.querySelectorAll('.media-viewer__rail-thumb').forEach((button) => {
          button.classList.toggle('active', button.getAttribute('data-media-id') === mediaId);
        });

        const activeSlide = this.querySelector(`.media-viewer__slide[data-media-id="${mediaId}"]`);
        if (!activeSlide) return;

        const deferredMedia = activeSlide.querySelector('deferred-media');
        if (deferredMedia) {
          const template = deferredMedia.querySelector('template');
          const content = template ? template.content : null;
          if (content && content.querySelector('.js-youtube')) deferredMedia.loadContent();
        }

        const image = activeSlide.querySelector('.media-viewer__image');
        if (image) {
          if (this.hoverCapable.matches) {
            image.style.transform = 'translateY(0)';
          } else {
            this.resetPinchZoom(image);
          }
        }
      }

      /* Vertical offset (px) that pans a tall image so the point at
         clientY sits centered — shared by the desktop hover-scrub
         (onMouseMove) and the touch-swipe scrub below, so both move a
         tall image the same way. */
      getScrubOffset(image, clientY) {
        const viewportHeight = window.innerHeight;
        const renderedHeight = image.getBoundingClientRect().height;
        const maxOffset = renderedHeight - viewportHeight;
        if (maxOffset <= 0) return 0;

        const ratio = Math.min(Math.max(clientY / viewportHeight, 0), 1);
        /* .media-viewer__stage centers an oversized image by default
           (align-items: center), so its untransformed position already
           sits at translateY(-maxOffset / 2), not 0. Without correcting
           for that baseline, ratio 0 cut off the image's top (still
           showing its centered position instead of the true top) and
           ratio 1 overshot past the bottom (subtracting the full offset
           from an already-centered start). */
        return maxOffset * (0.5 - ratio);
      }

      onMouseMove(event) {
        if (this.cursorIcon) {
          const stageRect = this.stage.getBoundingClientRect();
          this.cursorIcon.style.left = `${event.clientX - stageRect.left}px`;
          this.cursorIcon.style.top = `${event.clientY - stageRect.top}px`;
        }

        const activeSlide = this.querySelector('.media-viewer__slide.active');
        const image = activeSlide ? activeSlide.querySelector('.media-viewer__image') : null;
        if (!image) return;

        image.style.transform = `translateY(${this.getScrubOffset(image, event.clientY)}px)`;
      }

      /* ---- image_zoom: 'fullscreen' on touch — pinch between cover (default,
         fills the stage) and fit (the image's natural ratio at 100% stage
         width, centered); mutually exclusive with the desktop hover-pan
         above, gated the same way via hoverCapable. ---- */

      getFitHeight(image) {
        if (!this.stage) return 0;
        const width = parseFloat(image.getAttribute('width'));
        const height = parseFloat(image.getAttribute('height'));
        const stageWidth = this.stage.clientWidth;
        if (!width || !height || !stageWidth) return 0;
        return (stageWidth / width) * height;
      }

      /* >1 only for images wider than the stage (e.g. a landscape photo in
         a portrait stage) — the scale needed for the image to still cover
         the stage's height at scale 1. */
      getCoverScale(image) {
        const stageHeight = this.stage ? this.stage.clientHeight : 0;
        const fitHeight = this.getFitHeight(image);
        if (!stageHeight || fitHeight <= 0) return 1;
        return Math.max(1, stageHeight / fitHeight);
      }

      /* <1 only for images taller than the stage (this site's usual tall
         portrait product photos, which already overflow the stage height
         at scale 1) — the scale needed to shrink the image down until the
         whole thing (not just its width) fits within the stage. */
      getContainScale(image) {
        const stageHeight = this.stage ? this.stage.clientHeight : 0;
        const fitHeight = this.getFitHeight(image);
        if (!stageHeight || fitHeight <= 0) return 1;
        return Math.min(1, stageHeight / fitHeight);
      }

      resetPinchZoom(image) {
        /* Range spans both directions from the natural fit-to-width
           baseline (scale 1): pinch in down to containScale to reveal the
           whole image (containScale collapses to 1 — no room to zoom out —
           for images already shorter than the stage, which is correct,
           there's nothing more to reveal), pinch out up to a real zoom-in
           ceiling for detail (coverScale alone isn't enough range: it's
           also 1 for the common tall-portrait case, since scale 1 already
           covers the stage height, so force a floor on top of it). */
        const maxScale = Math.max(this.getCoverScale(image), 2.5);
        image.dataset.minScale = this.getContainScale(image);
        image.dataset.maxScale = maxScale;
        image.dataset.scale = 1;
        image.dataset.panX = 0;
        image.dataset.panY = 0;
        image.style.transform = 'translate(0px, 0px) scale(1)';
      }

      onResize() {
        if (!this.hasAttribute('open') || this.hoverCapable.matches) return;
        const activeSlide = this.querySelector('.media-viewer__slide.active');
        const image = activeSlide ? activeSlide.querySelector('.media-viewer__image') : null;
        if (image) this.resetPinchZoom(image);
      }

      /* Natural (scale 1) rendered size — the image is fit to the stage's
         width, so its base box is always stageWidth x getFitHeight(). Used
         to work out how far a zoomed image can pan before its edge would
         show empty space past the stage. */
      getBaseSize(image) {
        return { width: this.stage.clientWidth, height: this.getFitHeight(image) };
      }

      /* .media-viewer__stage centers the image (align-items/justify-content:
         center), so the image's own untransformed center always coincides
         with the stage's center — that shared point is the fixed reference
         every pan/zoom offset below is measured from. */
      getStageCenter() {
        const stageRect = this.stage.getBoundingClientRect();
        return { x: stageRect.left + stageRect.width / 2, y: stageRect.top + stageRect.height / 2 };
      }

      /* How far (in px, each direction) the image can be panned at a given
         scale before its edge would pull away from the stage and reveal
         empty space — half the overflow past the stage on that axis. */
      getPanBounds(image, scale) {
        const base = this.getBaseSize(image);
        return {
          x: Math.max(0, (scale * base.width - this.stage.clientWidth) / 2),
          y: Math.max(0, (scale * base.height - this.stage.clientHeight) / 2),
        };
      }

      setPinchTransform(image, scale, panX, panY) {
        const bounds = this.getPanBounds(image, scale);
        const clampedX = Math.min(bounds.x, Math.max(-bounds.x, panX));
        const clampedY = Math.min(bounds.y, Math.max(-bounds.y, panY));
        image.dataset.scale = scale;
        image.dataset.panX = clampedX;
        image.dataset.panY = clampedY;
        /* translate() before scale() so panX/panY are literal screen pixels
           at any zoom level — a finger moving 10px always moves the image
           10px, instead of the pan amount itself getting scaled up too. */
        image.style.transform = `translate(${clampedX}px, ${clampedY}px) scale(${scale})`;
      }

      /* Touch equivalent of the desktop hover-scrub: swipe position (not
         drag delta) pans a tall image, same formula as onMouseMove. Also
         writes scale/panX/panY so a pinch starting right after reads an
         accurate baseline instead of jumping. */
      applyTouchScrub(image, clientY) {
        const panY = this.getScrubOffset(image, clientY);
        image.dataset.scale = 1;
        image.dataset.panX = 0;
        image.dataset.panY = panY;
        image.style.transform = `translate(0px, ${panY}px) scale(1)`;
      }

      onPinchStart(event) {
        if (event.pointerType !== 'touch') return;
        this.activePointers.set(event.pointerId, event);
        /* Attached for every touch on the stage, not just ones that turn
           into a gesture — otherwise a touch that stays alone (no second
           finger) never gets its pointerup caught, leaving it stuck in
           activePointers and corrupting the pointer count for the next
           real gesture. */
        window.addEventListener('pointerup', this.onPinchEnd);
        window.addEventListener('pointercancel', this.onPinchEnd);

        const activeSlide = this.querySelector('.media-viewer__slide.active');
        const image = activeSlide ? activeSlide.querySelector('.media-viewer__image') : null;
        if (!image) return;

        if (this.activePointers.size === 1) {
          this.gestureImage = image;
          const scale = parseFloat(image.dataset.scale) || 1;
          if (scale > 1) {
            this.gestureMode = 'pan';
            const [p] = this.activePointers.values();
            this.panStart = { x: p.clientX, y: p.clientY };
            this.panStartOffset = { x: parseFloat(image.dataset.panX) || 0, y: parseFloat(image.dataset.panY) || 0 };
          } else {
            this.gestureMode = 'scrub';
          }
        } else if (this.activePointers.size === 2) {
          this.gestureMode = 'pinch';
          this.gestureImage = image;
          const [p1, p2] = Array.from(this.activePointers.values());
          this.pinchStartDistance = Math.hypot(p1.clientX - p2.clientX, p1.clientY - p2.clientY);
          this.pinchStartScale = parseFloat(image.dataset.scale) || 1;
          this.pinchStageCenter = this.getStageCenter();
          const midX = (p1.clientX + p2.clientX) / 2;
          const midY = (p1.clientY + p2.clientY) / 2;
          const panX0 = parseFloat(image.dataset.panX) || 0;
          const panY0 = parseFloat(image.dataset.panY) || 0;
          /* Anchor: the point on the (unscaled) image currently sitting
             under the pinch midpoint. Keeping this fixed as scale changes
             is what makes the pinch zoom into wherever you pinch instead of
             always the image center. */
          this.pinchAnchor = {
            x: (midX - this.pinchStageCenter.x - panX0) / this.pinchStartScale,
            y: (midY - this.pinchStageCenter.y - panY0) / this.pinchStartScale,
          };
        } else {
          return;
        }

        window.addEventListener('pointermove', this.onPinchMove, { passive: false });
      }

      onPinchMove(event) {
        if (!this.activePointers.has(event.pointerId)) return;
        this.activePointers.set(event.pointerId, event);

        if (this.gestureMode === 'scrub') {
          if (this.activePointers.size !== 1) return;
          event.preventDefault();
          const [p] = this.activePointers.values();
          this.applyTouchScrub(this.gestureImage, p.clientY);
          return;
        }

        if (this.gestureMode === 'pan') {
          if (this.activePointers.size !== 1) return;
          event.preventDefault();
          const [p] = this.activePointers.values();
          const scale = parseFloat(this.gestureImage.dataset.scale) || 1;
          const panX = this.panStartOffset.x + (p.clientX - this.panStart.x);
          const panY = this.panStartOffset.y + (p.clientY - this.panStart.y);
          this.setPinchTransform(this.gestureImage, scale, panX, panY);
          return;
        }

        if (this.gestureMode !== 'pinch' || this.activePointers.size < 2) return;

        event.preventDefault();
        const [p1, p2] = Array.from(this.activePointers.values());
        const distance = Math.hypot(p1.clientX - p2.clientX, p1.clientY - p2.clientY);
        const minScale = parseFloat(this.gestureImage.dataset.minScale) || 1;
        const maxScale = parseFloat(this.gestureImage.dataset.maxScale) || 1;
        const scale = Math.min(maxScale, Math.max(minScale, this.pinchStartScale * (distance / this.pinchStartDistance)));
        const midX = (p1.clientX + p2.clientX) / 2;
        const midY = (p1.clientY + p2.clientY) / 2;
        const panX = midX - this.pinchStageCenter.x - scale * this.pinchAnchor.x;
        const panY = midY - this.pinchStageCenter.y - scale * this.pinchAnchor.y;
        this.setPinchTransform(this.gestureImage, scale, panX, panY);
      }

      onPinchEnd(event) {
        const endedMode = this.gestureMode;
        const endedImage = this.gestureImage;
        this.activePointers.delete(event.pointerId);

        if (this.activePointers.size === 1 && endedMode === 'pinch' && endedImage) {
          /* Pinch dropped to one finger still down — hand off instead of
             ending the gesture, so the remaining finger keeps controlling
             the image (panning if still zoomed, scrubbing otherwise). */
          const scale = parseFloat(endedImage.dataset.scale) || 1;
          const [p] = this.activePointers.values();
          if (scale > 1) {
            this.gestureMode = 'pan';
            this.panStart = { x: p.clientX, y: p.clientY };
            this.panStartOffset = { x: parseFloat(endedImage.dataset.panX) || 0, y: parseFloat(endedImage.dataset.panY) || 0 };
          } else {
            this.gestureMode = 'scrub';
          }
          return;
        }

        if (this.activePointers.size >= 2) return;

        /* Lifting one finger of a pinch can leave the other still down —
           its pointerup won't be caught once these listeners are removed,
           so it'd linger as a stale pointer and get paired with the next
           single-finger touch, misreading a swipe as a pinch. Clear it. */
        this.activePointers.clear();
        window.removeEventListener('pointermove', this.onPinchMove);
        window.removeEventListener('pointerup', this.onPinchEnd);
        window.removeEventListener('pointercancel', this.onPinchEnd);
        this.gestureMode = null;
        this.gestureImage = null;
      }

      /* ---- image_zoom: 'lightbox' (default) — horizontal filmstrip modal ---- */

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }
    }
  );
}
